import { useState, useEffect } from 'react';
import { DocumentTextIcon, PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import type { Flow } from '../../types';
import EditorDocumento from '../EditorDocumento';

interface DocumentosProps {
  flow: Flow;
  onClose: () => void;
  userPermissions?: {
    canAdmin?: boolean;
    canEdit?: boolean;
    canView?: boolean;
    currentUser?: {
      id?: string;
      role?: string;
      nome?: string;
    };
  };
}

interface TemplateDocumento {
  _id?: string;
  id: string;
  nome: string;
  descricao: string;
  flowId: string;
  flowNome: string;
  etapaId?: string; // Opcional - documento pode ser do fluxo todo ou de uma etapa específica
  etapaNome?: string;
  tipo?: 'html' | 'pdf'; // 'html' para editor com campos dinâmicos, 'pdf' para PDF importado
  conteudoHtml: string;
  pdfUrl?: string;
  pdfNomeOriginal?: string;
  empresaId: string;
  usuarioId: string;
  dataCriacao: string;
  dataAtualizacao: string;
  ativo: boolean;
}

const Documentos = ({ flow, onClose, userPermissions }: DocumentosProps) => {
  const [templates, setTemplates] = useState<TemplateDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<TemplateDocumento | null>(null);
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [visualizando, setVisualizando] = useState<TemplateDocumento | null>(null);

  // Verificar se usuário é admin - apenas admins podem criar/editar/excluir templates
  const isAdmin = userPermissions?.canAdmin || userPermissions?.currentUser?.role === 'administrator';

  useEffect(() => {
    carregarTemplates();
  }, [flow.id]);

  const carregarTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/documentos/templates?flowId=${flow.id}&empresaId=${flow.empresaId}`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
        console.log('📄 Templates carregados:', data.length);
      } else {
        console.error('Erro ao carregar templates:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar templates de documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const salvarTemplate = async (template: any) => {
    try {
      const templateData = {
        ...template,
        empresaId: flow.empresaId,
        usuarioId: 'current-user-id' // TODO: pegar do contexto
      };

      // Só considerar como edição se tiver _id do MongoDB (24 caracteres)
      const isEdit = template._id && typeof template._id === 'string' && template._id.length === 24;
      const url = isEdit 
        ? `/api/documentos/templates/${template._id}`
        : '/api/documentos/templates';
      
      const method = isEdit ? 'PUT' : 'POST';
      
      console.log('💾 Salvando template:', { isEdit, url, method, templateData });

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Template salvo:', data);
        await carregarTemplates();
        return data;
      } else {
        const error = await response.json();
        console.error('Erro ao salvar template:', error);
        alert(`Erro: ${error.error || 'Erro ao salvar template'}`);
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template');
    }
  };

  const excluirTemplate = async (templateId: string) => {
    if (!confirm('Deseja realmente excluir este template?')) {
      return;
    }

    try {
      const response = await fetch(`/api/documentos/templates/${templateId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('🗑️ Template excluído');
        await carregarTemplates();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.error || 'Erro ao excluir template'}`);
      }
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      alert('Erro ao excluir template');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[75vh] overflow-hidden flex flex-col border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-indigo-50 rounded-lg">
              <DocumentTextIcon className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Documentos</h2>
              <p className="text-xs text-gray-500">
                Fluxo: {flow.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setCriandoNovo(true)}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-xs font-medium"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Novo Template
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-xs">Carregando templates...</p>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <div className="p-2.5 bg-gray-100 rounded-full mb-3">
                <DocumentTextIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Nenhum template de documento
              </h3>
              <p className="text-gray-500 text-xs mb-3 max-w-sm">
                {isAdmin 
                  ? 'Crie templates de documentos timbrados gerados automaticamente com os campos dos cards.'
                  : 'Não há templates configurados para este fluxo.'
                }
              </p>
              {isAdmin && (
                <button
                  onClick={() => setCriandoNovo(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 text-xs font-medium"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Criar Primeiro Template
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map(template => (
                <div
                  key={template.id}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-indigo-200 transition-all group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm truncate">
                        {template.nome}
                      </h3>
                      {template.descricao && (
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                          {template.descricao}
                        </p>
                      )}
                    </div>
                    <div className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ml-2 flex-shrink-0 ${
                      template.ativo 
                        ? 'bg-emerald-50 text-emerald-600' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {template.ativo ? 'Ativo' : 'Inativo'}
                    </div>
                  </div>

                  {template.etapaNome && (
                    <div className="mb-2 text-[10px] text-gray-400">
                      📍 Etapa: <span className="font-medium text-gray-500">{template.etapaNome}</span>
                    </div>
                  )}

                  {template.tipo === 'pdf' && (
                    <div className="mb-2 text-[10px] text-blue-500 font-medium">
                      📄 PDF importado
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setVisualizando(template)}
                      className="flex-1 px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors text-xs font-medium flex items-center justify-center gap-1"
                    >
                      <EyeIcon className="w-3 h-3" />
                      Ver
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => setEditando(template)}
                          className="flex-1 px-2 py-1 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 transition-colors text-xs font-medium flex items-center justify-center gap-1"
                        >
                          <PencilIcon className="w-3 h-3" />
                          Editar
                        </button>
                        <button
                          onClick={() => excluirTemplate(template._id || template.id)}
                          className="px-2 py-1 bg-red-50 text-red-500 rounded-md hover:bg-red-100 transition-colors text-xs"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criar/Editar */}
      {(criandoNovo || editando) && (
        <EditorDocumento
          flow={flow}
          template={editando}
          onSave={async (template) => {
            await salvarTemplate(template);
            setCriandoNovo(false);
            setEditando(null);
          }}
          onClose={() => {
            setCriandoNovo(false);
            setEditando(null);
          }}
        />
      )}

      {/* Modal de Visualização */}
      {visualizando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{visualizando.nome}</h2>
                {visualizando.descricao && (
                  <p className="text-blue-100 text-sm mt-1">{visualizando.descricao}</p>
                )}
              </div>
              <button
                onClick={() => setVisualizando(null)}
                className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {visualizando.tipo === 'pdf' && visualizando.pdfUrl ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
                  <iframe
                    src={visualizando.pdfUrl}
                    className="w-full h-full min-h-[60vh] rounded-lg"
                    title={`PDF - ${visualizando.nome}`}
                  />
                </div>
              ) : (
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: visualizando.conteudoHtml }}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-100 px-6 py-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {visualizando.etapaNome && (
                  <span>📍 Etapa: <strong>{visualizando.etapaNome}</strong></span>
                )}
              </div>
              <div className="flex gap-2">
                {isAdmin && (
                  <button
                    onClick={() => {
                      setVisualizando(null);
                      setEditando(visualizando);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Editar
                  </button>
                )}
                <button
                  onClick={() => setVisualizando(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentos;
